import React, { useEffect, useState } from "react";
import { motionSafe as motion } from "@/lib/motionSafe";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Timer } from "lucide-react";
import { useI18n } from "../../i18n";

// Two hard-wired CRA deadlines. Values are UTC midnight (start of the day).
const DEADLINES = [
  { key: "vulnReporting", date: Date.UTC(2026, 8, 11) }, // 11 Sep 2026
  { key: "coreObligations", date: Date.UTC(2027, 11, 11) }, // 11 Dec 2027
];

const L = {
  de: {
    overline: "CRA Countdown",
    vulnReporting: "Meldepflicht Schwachstellen",
    coreObligations: "CRA-Kernpflichten",
    d: "Tage", h: "Std", m: "Min", s: "Sek",
    passed: "Frist verstrichen",
  },
  en: {
    overline: "CRA Countdown",
    vulnReporting: "Vulnerability Reporting",
    coreObligations: "CRA Core Obligations",
    d: "Days", h: "Hrs", m: "Min", s: "Sec",
    passed: "Deadline passed",
  },
};

const pad = (n) => String(n).padStart(2, "0");

const diffParts = (target) => {
  const now = Date.now();
  let ms = target - now;
  const passed = ms <= 0;
  if (passed) ms = 0;
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return { d, h, m, s, passed };
};

const Segment = ({ value, label, mono = false }) => (
  <div className="flex flex-col items-center min-w-[52px] md:min-w-[64px]">
    {/* suppressHydrationWarning: the countdown value legitimately differs
        between prerender time and hydration time; React keeps the prerendered
        text and the 1s interval patches it right after hydration. */}
    <div suppressHydrationWarning className={`font-display font-extrabold tabular-nums text-2xl md:text-3xl text-white leading-none ${mono ? "font-mono-tech" : ""}`}>
      {value}
    </div>
    <div className="mt-1.5 font-mono-tech text-[9px] md:text-[10px] tracking-[0.2em] uppercase text-gray-400">
      {label}
    </div>
  </div>
);

const DeadlineRow = ({ target, dateISO, dateLabel, title, l, srpCta }) => {
  const [t, setT] = useState(() => diffParts(target));

  useEffect(() => {
    const id = setInterval(() => setT(diffParts(target)), 1000);
    return () => clearInterval(id);
  }, [target]);

  return (
    <div className="py-4 border-t border-white/10 first:border-t-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="font-display text-sm md:text-base font-bold text-white leading-tight">
            {title}
          </div>
          <time
            dateTime={dateISO}
            className="mt-1 block font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#3B82F6]"
          >
            {dateLabel}
          </time>
        </div>
        {t.passed ? (
          <div className="inline-flex items-center gap-2 font-mono-tech text-[11px] tracking-[0.2em] uppercase text-amber-400">
            <AlertTriangle size={14} /> {l.passed}
          </div>
        ) : (
          <div className="flex items-start gap-3 md:gap-4">
            <Segment value={t.d} label={l.d} />
            <div className="pt-1 text-gray-400 text-lg font-display">:</div>
            <Segment value={pad(t.h)} label={l.h} mono />
            <div className="pt-1 text-gray-400 text-lg font-display">:</div>
            <Segment value={pad(t.m)} label={l.m} mono />
            <div className="pt-1 text-gray-400 text-lg font-display">:</div>
            <Segment value={pad(t.s)} label={l.s} mono />
          </div>
        )}
      </div>
      {srpCta && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to="/srp"
            data-testid="home-srp-prepare-cta"
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-sm bg-[#0052FF] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2 focus:ring-offset-[#07111F]"
          >
            {srpCta.label} <ArrowRight size={14} />
          </Link>
          <span className="font-mono-tech text-[10px] tracking-[0.18em] uppercase text-gray-400">
            24h · 72h · Final Report
          </span>
        </div>
      )}
    </div>
  );
};

export const CraDeadlineCountdown = () => {
  const { lang } = useI18n();
  const l = L[lang] || L.en;
  const dateLabels = {
    vulnReporting: lang === "de" ? "11. September 2026" : "11 September 2026",
    coreObligations: lang === "de" ? "11. Dezember 2027" : "11 December 2027",
  };
  const dateISOs = { vulnReporting: "2026-09-11", coreObligations: "2027-12-11" };

  return (
    <motion.div
      initial={{ opacity: 1, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.05, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="mt-10 relative max-w-2xl glass rounded-sm px-5 md:px-6 py-4 md:py-5 overflow-hidden"
      data-testid="cra-countdown"
    >
      <div className="absolute -top-24 -left-24 w-56 h-56 rounded-full bg-[#0052FF]/20 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="inline-flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[#3B82F6] mb-1">
          <Timer size={12} /> {l.overline}
        </div>
        {DEADLINES.map((d) => (
          <DeadlineRow
            key={d.key}
            target={d.date}
            dateISO={dateISOs[d.key]}
            dateLabel={dateLabels[d.key]}
            title={l[d.key]}
            l={l}
            srpCta={
              d.key === "vulnReporting"
                ? { label: lang === "de" ? "SRP-Meldung vorbereiten" : "Prepare SRP notification" }
                : null
            }
          />
        ))}
      </div>
    </motion.div>
  );
};
